"use client";

import type { PropsWithChildren } from "react";
import { useEffect, useRef } from "react";
import type {
  DefaultError,
  MutationCacheNotifyEvent,
  QueryCacheNotifyEvent
} from "@tanstack/react-query";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AxiosError } from "axios";
import useUser from "@hooks/user.hook";
import { translateWithFallback } from "@src/core/i18n";
import useMessage from "../../src/hooks/message.hook";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      throwOnError: false, // Errors are only visible by console.error
      staleTime: 1000 * 60 * 5, // After 5 minutes, data becomes stale and re-fetch may occur
      gcTime: 1000 * 60 * 5 // After 5 minutes of no component using the data, it is removed from memory
    }
  }
});

export default function TanstackQueryProvider({
  children
}: Readonly<PropsWithChildren>) {
  const { addError } = useMessage();
  const { isAdmin } = useUser();

  // Users shouldn't be bombarded with the same error message,
  // although this could also be handled in useMessage?
  const loggedQueryErrors = useRef(new Set<string>());
  const loggedMutationErrors = useRef(new Set<number>());

  useEffect(() => {
    const unsubscribeQuery = queryClient
      .getQueryCache()
      .subscribe((event: QueryCacheNotifyEvent) => {
        const queryKey = event.query.queryHash;
        const state = event.query.state;
        const error = state.error as DefaultError;

        if (
          state.status !== "error" ||
          loggedQueryErrors.current.has(queryKey)
        ) {
          return;
        }

        const label = formatErrorMessage(error);
        loggedQueryErrors.current.add(queryKey);
        addError({ content: label, consoleOnly: !isAdmin });
      });

    const unsubscribeMutation = queryClient
      .getMutationCache()
      .subscribe((event: MutationCacheNotifyEvent) => {
        if (!event.mutation) return;

        const mutationKey = event.mutation.mutationId;
        const state = event.mutation.state;
        const error = state.error as DefaultError;

        if (
          state.status !== "error" ||
          loggedMutationErrors.current.has(mutationKey)
        ) {
          return;
        }

        const label = formatErrorMessage(error);
        loggedMutationErrors.current.add(mutationKey);
        addError({ content: label, consoleOnly: !isAdmin });
      });

    return () => {
      unsubscribeQuery();
      unsubscribeMutation();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}

function formatErrorMessage(error: DefaultError) {
  if (error instanceof AxiosError) {
    const statusCode: number | undefined = error.response?.status;
    const axiosMessage: string = error.message;
    const translationKey = `form:MESSAGE.HTTP.STATUS_${statusCode}`;
    return translateWithFallback(translationKey, axiosMessage);
  }

  return error?.message || "Unknown error";
}
